import { PII_SENSITIVE_SENTENCE, PII_WHAT_FORMS, TOOL_CALL_THREAT_LABELS, TYPOSQUAT_IMITATION, TYPOSQUAT_NO_SIGNAL_WHAT, } from '../../data/alert-risk-text';
import { clientDisplayName, fallbackOf, fillRiskTemplate, MAX_MATCHED_LENGTH, partsOf, present, riskCopy, } from './common';
// Personal data (sheet rows 107, 109, 111), packages (115, 118) and the
// tool-call scan (120–125).
const PII_DESCRIPTION = /^(?:PII detected|Multiple PIIs detected|Bulk PIIs detected)(?: (.*))?: (?:More than )?(\d+) PIIs?$/s;
/** {source} from the producer's fixed phrase (pii-detection `sourceDetailPhrase`). */
export function piiSource(phrase) {
    const text = phrase?.trim() ?? '';
    if (text === 'in prompt')
        return 'in a prompt entered in the coding agent';
    const file = text.match(/^in file (.+)$/s);
    if (file?.[1] != null)
        return `in content read from the file ${file[1]}`;
    const tool = text.match(/^in tool (.+) output$/s);
    if (tool?.[1] != null) {
        return tool[1].startsWith('mcp__')
            ? `in output returned by the MCP tool \`${tool[1]}\``
            : `in output returned by the tool \`${tool[1]}\``;
    }
    if (/^in terminal command `.*`$/s.test(text))
        return 'in terminal command content';
    return 'in content handled by the coding agent';
}
/** Up to three labels, then "1 more type" / "{k} more types", joined with "and". */
export function piiTypeList(labels) {
    if (labels.length === 0)
        return undefined;
    const shown = labels.slice(0, 3);
    const rest = labels.length - shown.length;
    const items = rest === 0
        ? shown
        : [...shown, rest === 1 ? '1 more type' : `${rest} more types`];
    return items.length === 1
        ? items[0]
        : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
export function resolvePii(alert) {
    const copy = riskCopy(`piiDetection::${alert.type}`);
    const match = alert.description.trim().match(PII_DESCRIPTION);
    const source = piiSource(match?.[1]);
    const matched = alert.sourceDetails?.kind === 'pii'
        ? alert.sourceDetails.matchedTypes
        : undefined;
    const typeList = piiTypeList((matched ?? []).map(t => t.label));
    const n = match?.[2] == null ? undefined : Number(match[2]);
    const form = n == null
        ? PII_WHAT_FORMS.noCount
        : n === 1
            ? typeList == null
                ? PII_WHAT_FORMS.oneWithoutTypes
                : PII_WHAT_FORMS.oneWithTypes
            : typeList == null
                ? PII_WHAT_FORMS.manyWithoutTypes
                : PII_WHAT_FORMS.manyWithTypes;
    const why = alert.type === 'pii-detected' && !(matched ?? []).some(t => t.sensitive)
        ? copy.whyItMatters.replace(PII_SENSITIVE_SENTENCE, '')
        : copy.whyItMatters;
    return partsOf(copy, fillRiskTemplate(form, { n: n?.toString(), typeList, source }), why);
}
const PACKAGE_LOCATION = /^Package: (.+) \(([^()]+)\)$/;
function packageOf(location) {
    const match = location?.trim().match(PACKAGE_LOCATION);
    const name = present(match?.[1]);
    const ecosystem = present(match?.[2]);
    return name == null || ecosystem == null ? undefined : { name, ecosystem };
}
/** Row 115. A missing or literal "unknown" advisory id drops the parenthetical. */
export function resolveMaliciousPackage(alert) {
    const copy = riskCopy('maliciousPackagesKnownMalware::malicious_package');
    const pkg = packageOf(alert.location);
    if (pkg == null)
        return fallbackOf(copy);
    const fromDetails = alert.sourceDetails?.kind === 'package'
        ? alert.sourceDetails.osvId
        : undefined;
    const osvId = present(fromDetails) ??
        (alert.description.length > MAX_MATCHED_LENGTH
            ? undefined
            : present(alert.description.match(/as a MALICIOUS package \(([^)]+)\)/)?.[1]));
    const template = osvId == null || osvId.toLowerCase() === 'unknown'
        ? copy.whatHappened.replace(' ({osvId})', '')
        : copy.whatHappened;
    return partsOf(copy, fillRiskTemplate(template, { ...pkg, osvId }), copy.whyItMatters);
}
/**
 * Row 113. {target} and the signal come from source details (alert_source_details);
 * a missing target or signal uses the generic sentence. {name} is the
 * canonical name the detector recorded.
 */
export function resolveTyposquat(alert) {
    const copy = riskCopy('maliciousPackagesTyposquatting::package_typosquatting');
    const pkg = packageOf(alert.location);
    if (pkg == null)
        return fallbackOf(copy);
    const details = alert.sourceDetails?.kind === 'package' ? alert.sourceDetails : undefined;
    const target = present(details?.typosquatTarget);
    // detectionReason is "<signal> against <target> (popularity rank N)".
    const signal = present(details?.detectionReason)?.split(' against ')[0];
    // Row 113: confusable-substitution → look-alike; any other signal → keystroke.
    const imitation = signal == null
        ? undefined
        : (TYPOSQUAT_IMITATION[signal] ?? TYPOSQUAT_IMITATION['single-edit']);
    const what = target == null || imitation == null
        ? fillRiskTemplate(TYPOSQUAT_NO_SIGNAL_WHAT, pkg)
        : fillRiskTemplate(copy.whatHappened, { ...pkg, target, imitation });
    return partsOf(copy, what, copy.whyItMatters);
}
/** Row 118. Without a known client the sentence starts "The coding agent". */
export function resolveBlockedPackage(alert) {
    const copy = riskCopy('packagesBlockedList::blocked_by_policy');
    const pkg = packageOf(alert.location);
    if (pkg == null)
        return fallbackOf(copy);
    return partsOf(copy, fillRiskTemplate(copy.whatHappened, {
        ...pkg,
        client: clientDisplayName(alert.clientType) ?? 'The coding agent',
    }), copy.whyItMatters);
}
/** Trim, case-fold, underscores to spaces, collapse whitespace (column T). */
export function normalizeThreatLabel(label) {
    return (label ?? '')
        .trim()
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ');
}
/** Rows 120–125: exact label match after normalization, anything else is T0. */
export function resolveToolCall(alert) {
    const variant = TOOL_CALL_THREAT_LABELS[normalizeThreatLabel(alert.toolCallScan?.topThreatLabel)] ?? 'T0';
    const copy = riskCopy(`toolCallScanMode::tool-call-scan#${variant}`);
    const tool = present(alert.location);
    return partsOf(copy, fillRiskTemplate(copy.whatHappened, {
        client: clientDisplayName(alert.clientType),
        toolPhrase: tool == null ? 'a tool call' : `a "${tool}" tool call`,
    }), copy.whyItMatters);
}
//# sourceMappingURL=pii-packages-tools.js.map